import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { FaFilter } from 'react-icons/fa';
import CloseIcon from '@components/SvgComponents/CloseIcon';
import { Doughnut } from 'react-chartjs-2';
import { Line } from 'react-chartjs-2';
import Select, { SingleValue } from 'react-select';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    Plugin,
    ChartOptions,
} from 'chart.js';
import {
    getHospitalInfusionDeviceData,
    getIndividualInfusionDeviceData,
    getNurseInfusionDeviceData,
    deviceChangeLogInfusionData,
    fetchDeviceChangeLogInfusionData,
    getDataUsingDeviceUuid,
    hospitalUserInfo,
} from '@utils/hospitalUtils';
import { getDataUsingDeviceId, userDetail } from '@utils/sharedUtils';
import { getDeviceData, fetchAnalyticsChartApi } from '@utils/deviceUtils';
import {
    getAllDeviceIds,
    getAllIndividualDeviceIds,
    getCompletedInfusionCountData,
    getCompletedInfusionCountDataUsingTenant,
} from '@utils/utils';
import {
    DEVICE_CONTROL_TABLE,
    DEVICE_TABLE,
    DEVICE_LIVE_DATA_TABLE,
    NURSE_DEVICE_TABLE,
    HOSPITAL_DEVICE_TABLE,
} from '@constants/dynamoDBTableName';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { HOSPITAL, NURSE, INDIVIDUAL } from '@constants/constants';
import { setReportData } from '@store/reducers/userSlice';
import { useDispatch } from 'react-redux';
type CustomDropdownToggleProps = {
    children?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};
const CustomDropdownToggle = React.forwardRef<
    HTMLButtonElement,
    CustomDropdownToggleProps
>(({ children, onClick }, ref) => (
    <button
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick?.(e);
        }}
        className="btn !bg-primary py-2 text-white mr-3"
    >
        {children}
        <FaFilter className="ml-2" /> {/* Add your custom icon here */}
    </button>
));
// ChartJS.register(ArcElement, Tooltip, Legend);
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    ArcElement,
    Legend
);
interface DeviceData {
    device_id: string;
    device_name: string;
    // Add other fields as needed...
}
type DeviceOption = { value: string; label: string } | null;

const Analyticschart = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [userRole, setUserRole] = useState('');
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [inActiveDeviceCount, setInActiveDeviceCount] = useState<
        number | null
    >(null);
    const [activeDeviceCount, setActiveDeviceCount] = useState<number | null>(
        null
    );
    const [nonFunctionalDeviceCount, setNonFunctionalDeviceCount] = useState<
        number | null
    >(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedPeriod, setSelectedPeriod] = useState<string>('today');
    const loggedInHospitalUuId = useAppSelector(
        (state) => state.login.loggedInHospitalUuId
    );
    const loggedInUserRole = useAppSelector(
        (state) => state.login.loggedInUserRole
    );
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const loggedInUserID = useAppSelector(
        (state) => state.login.loggedInUserID
    );
    const [infusionDeviceData, setInfusionDeviceData] = useState<number[]>([]); // Chart data
    const [selectedDeviceOption, setSelectedDeviceOption] =
        useState<DeviceOption>(null);
    const [startDate, setStartDate] = useState<string>(''); // Start date state
    const [volumeConsumed, setVolumeConsumed] = useState<number>(0);
    const [endDate, setEndDate] = useState<string>(''); // End date state
    const [deviceData, setDeviceData] = useState<DeviceData[]>([]);
    const [chartData, setChartData] = useState<{ [key: string]: number }>({});

    const createCompleteDataMap = (
        period: string,
        existingData: { [key: string]: number }
    ): { [key: string]: number } => {
        const completeMap: { [key: string]: number } = {};
        const now = new Date();
        let startDate: Date, endDate: Date;

        switch (period) {
            case 'today':
                for (let i = 0; i < 24; i++) {
                    const key = `${i.toString().padStart(2, '0')}:00`;
                    completeMap[key] = existingData[key] || 0;
                }
                break;
            case 'this_week':
            case 'last_week': {
                const baseDate = new Date(now);
                baseDate.setDate(baseDate.getDate() - baseDate.getDay());
                if (period === 'last_week') {
                    baseDate.setDate(baseDate.getDate() - 7);
                }
                for (let i = 0; i < 7; i++) {
                    const date = new Date(baseDate);
                    date.setDate(date.getDate() + i);
                    const key = formatDate(date, period);
                    completeMap[key] = existingData[key] || 0;
                }
                break;
            }
            case 'this_month':
            case 'last_month': {
                startDate = new Date(
                    now.getFullYear(),
                    period === 'this_month'
                        ? now.getMonth()
                        : now.getMonth() - 1,
                    1
                );
                endDate = new Date(
                    startDate.getFullYear(),
                    startDate.getMonth() + 1,
                    0
                );
                for (let i = 1; i <= endDate.getDate(); i++) {
                    const date = new Date(
                        startDate.getFullYear(),
                        startDate.getMonth(),
                        i
                    );
                    const key = formatDate(date, period);
                    completeMap[key] = existingData[key] || 0;
                }
                break;
            }
            default:
                return existingData;
        }

        return completeMap;
    };

    const fetchDevicesList = async (): Promise<DeviceData[]> => {
        try {
            let devices: DeviceData[] = [];
            if (loggedInUserRole.toLowerCase() === HOSPITAL.toLowerCase()) {
                devices = await getHospitalInfusionDeviceData(
                    HOSPITAL_DEVICE_TABLE,
                    loggedInHospitalUuId
                );
            } else if (loggedInUserRole.toLowerCase() === INDIVIDUAL.toLowerCase()) {
                devices = await getIndividualInfusionDeviceData(loggedInUserTenantID);
            } else if (loggedInUserRole.toLowerCase() === NURSE.toLowerCase()) {
                devices = await getNurseInfusionDeviceData(
                    NURSE_DEVICE_TABLE,
                    loggedInUserID
                );
            }

            if (devices && Array.isArray(devices)) {
                setDeviceData(devices);
            }
            return devices || [];
        } catch (e) {
            console.warn('Error fetching device list:', e);
            return [];
        }
    };

    const loadFallbackAnalyticsData = async (
        period: string,
        customParams?: { device_id?: string; start_date?: string; end_date?: string }
    ) => {
        try {
            let devices: DeviceData[] = deviceData;
            if (!devices || devices.length === 0) {
                devices = await fetchDevicesList();
            }

            if (devices && devices.length > 0) {
                let active = 0, inactive = 0, nonFunctional = 0;
                for (const dev of devices) {
                    const status = ((dev as any).device_status || (dev as any).status || '').toLowerCase();
                    if (status === 'active') active++;
                    else if (status === 'inactive') inactive++;
                    else if (status === 'non functional' || status === 'non-functional') nonFunctional++;
                }

                setActiveDeviceCount((prev) => (prev !== null ? prev : active));
                setInActiveDeviceCount((prev) => (prev !== null ? prev : inactive));
                setNonFunctionalDeviceCount((prev) => (prev !== null ? prev : nonFunctional));

                const dataMap: { [key: string]: number } = {};

                if (customParams?.device_id && customParams?.start_date && customParams?.end_date) {
                    const logData = await fetchDeviceChangeLogInfusionData(
                        customParams.device_id,
                        customParams.start_date,
                        customParams.end_date
                    );
                    logData?.forEach((log: any) => {
                        const dateVal = log?.created_at?.S || log?.created_at;
                        if (!dateVal) return;
                        const date = new Date(dateVal);
                        const formattedDate = formatDate(date, 'custom');
                        const volVal = log?.meta_data?.M?.volume_consumed?.N
                            ?? log?.meta_data?.volume_consumed
                            ?? log?.volume_consumed
                            ?? log?.Volume?.N
                            ?? log?.Volume
                            ?? log?.T_Qua?.N
                            ?? log?.T_Qua
                            ?? 0;
                        const volume = parseFloat(volVal) || 0;
                        dataMap[formattedDate] = (dataMap[formattedDate] || 0) + volume;
                    });
                    setChartData(dataMap);
                } else {
                    for (const device of devices) {
                        if (!device.device_id) continue;
                        const infusionLog = await deviceChangeLogInfusionData(device.device_id);
                        const filtered = filterDataByPeriod(infusionLog || [], period);
                        filtered.forEach(({ date, volume }) => {
                            const formattedDate = formatDate(date, period);
                            dataMap[formattedDate] = (dataMap[formattedDate] || 0) + volume;
                        });
                    }
                    const completeMap = createCompleteDataMap(period, dataMap);
                    setChartData(completeMap);
                }
            }
        } catch (fallbackError) {
            console.error('Fallback analytics fetch error:', fallbackError);
        }
    };

    const loadAnalyticsData = async (
        period: string = selectedPeriod,
        customParams?: { device_id?: string; start_date?: string; end_date?: string }
    ) => {
        let apiDataLoaded = false;
        fetchDevicesList();

        try {
            const response = await fetchAnalyticsChartApi({
                period,
                ...customParams,
            });

            if (response) {
                const res = response?.data || response?.counts || response?.summary || response?.overview || response;

                const active = res?.activeDeviceCount ?? res?.active_device_count ?? res?.activeDevice ?? res?.active_device ?? res?.activeCount ?? res?.active_count ?? res?.active;
                const inactive = res?.inActiveDeviceCount ?? res?.inactiveDeviceCount ?? res?.inactive_device_count ?? res?.inactiveDevice ?? res?.inactive_device ?? res?.inactiveCount ?? res?.inactive_count ?? res?.inactive;
                const nonFunctional = res?.nonFunctionalDeviceCount ?? res?.non_functional_device_count ?? res?.nonFunctionalDevice ?? res?.non_functional_device ?? res?.nonFunctionalCount ?? res?.non_functional_count ?? res?.nonFunctional ?? res?.non_functional;

                if (active !== undefined || inactive !== undefined || nonFunctional !== undefined) {
                    setActiveDeviceCount(Number(active) || 0);
                    setInActiveDeviceCount(Number(inactive) || 0);
                    setNonFunctionalDeviceCount(Number(nonFunctional) || 0);
                }

                const rawChart = res?.infusionDeviceDatas ?? response?.infusionDeviceDatas ?? res?.chartData ?? response?.chartData;

                if (Array.isArray(rawChart) && rawChart.length > 0) {
                    const dataMap: { [key: string]: number } = {};
                    rawChart.forEach((item: any) => {
                        const dateKey = item?.date || item?.label;
                        const vol = parseFloat(item?.value ?? item?.volume ?? 0) || 0;
                        if (dateKey) {
                            dataMap[dateKey] = (dataMap[dateKey] || 0) + vol;
                        }
                    });
                    const completeMap = createCompleteDataMap(period, dataMap);
                    setChartData(completeMap);
                    apiDataLoaded = true;
                } else if (rawChart && typeof rawChart === 'object' && Object.keys(rawChart).length > 0) {
                    const completeMap = createCompleteDataMap(period, rawChart);
                    setChartData(completeMap);
                    apiDataLoaded = true;
                }

                if (response.deviceData && Array.isArray(response.deviceData) && response.deviceData.length > 0) {
                    setDeviceData(response.deviceData);
                }
            }
        } catch (err: any) {
            console.warn('Analytics API unavailable or CORS blocked. Falling back to database fetch:', err);
        }

        if (!apiDataLoaded) {
            await loadFallbackAnalyticsData(period, customParams);
        }
    };

    useEffect(() => {
        if (loggedInUserRole !== '') {
            setUserRole(loggedInUserRole);
        }
        loadAnalyticsData(selectedPeriod);
    }, [loggedInUserRole, selectedPeriod]);

    const handleToggle = (show: boolean) => {
        setIsDropdownVisible(show);
        resetForm();
    };

    const handleSelectChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const selectedPeriod = event.target.value;
        setSelectedPeriod(selectedPeriod);
        loadAnalyticsData(selectedPeriod);
    };
    const generateDateLabels = (startDate: string, endDate: string) => {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const dateArray: string[] = [];

        const options: Intl.DateTimeFormatOptions = {
            month: 'short',
            day: '2-digit',
        };

        // Loop through the dates from start to end
        while (start <= end) {
            dateArray.push(start.toLocaleDateString('en-US', options)); // Format the date as 'Sep 02'
            start.setDate(start.getDate() + 1); // Increment the day
        }

        return dateArray;
    };
    const filterDataByPeriod = (data: any[], period: string) => {
        const now = new Date();

        let startDate: Date, endDate: Date;

        switch (period) {
            case 'today':
                startDate = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate(),
                    0,
                    0,
                    0
                );
                endDate = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate(),
                    23,
                    59,
                    59
                );
                break;
            case 'this_week':
                startDate = new Date(now);
                startDate.setDate(now.getDate() - now.getDay());
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date(now);
                break;
            case 'last_week':
                startDate = new Date(now);
                startDate.setDate(now.getDate() - now.getDay() - 7);
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date(startDate);
                endDate.setDate(startDate.getDate() + 6);
                endDate.setHours(23, 59, 59, 999);
                break;
            case 'this_month':
                startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
                endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
                break;
            case 'last_month':
                startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
                endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
                break;
            default:
                console.error('Invalid period:', period);
                return [];
        }

        const filteredData = data
            .filter((item) => {
                const dateStr = item?.created_at?.S || item?.created_at;
                if (!dateStr) return false;
                const itemDate = new Date(dateStr);
                return itemDate >= startDate && itemDate <= endDate;
            })
            .map((item) => {
                const dateStr = item?.created_at?.S || item?.created_at;
                const volVal = item?.meta_data?.M?.volume_consumed?.N
                    ?? item?.meta_data?.volume_consumed
                    ?? item?.volume_consumed
                    ?? item?.volume
                    ?? 0;
                return {
                    date: new Date(dateStr),
                    volume: parseFloat(volVal) || 0,
                };
            });

        return filteredData;
    };
    const formatDate = (date: Date, period: string): string => {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const months = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ];

        switch (period) {
            case 'today':
                return `${date.getHours().toString().padStart(2, '0')}:00`;
            case 'this_week':
            case 'last_week':
                return `${days[date.getDay()]} ${date.getDate().toString().padStart(2, '0')}`;
            case 'this_month':
            case 'last_month':
                return `${months[date.getMonth()]} ${date.getDate().toString().padStart(2, '0')}`;
            case 'custom':
                return `${months[date.getMonth()]} ${date.getDate().toString().padStart(2, '0')}`;
            default:
                return `${days[date.getDay()]} ${date.getDate().toString().padStart(2, '0')}`;
        }
    };
    const handleClose = () => {
        setIsDropdownVisible(false);
    };
    const resetForm = () => {
        setSelectedDeviceOption(null); // Clear device ID
        setStartDate(''); // Clear start date
        setEndDate(''); // Clear end date
    };
    // const resetChartData = () => {
    //     setVolumeConsumed(0); // Reset the volume consumed
    //     setInfusionDeviceData([]); // Clear chart data
    //   };
    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!selectedDeviceOption || !startDate || !endDate) {
            setError('Please select a device, start date, and end date');
            return;
        }

        try {
            await loadAnalyticsData('custom', {
                device_id: selectedDeviceOption.value,
                start_date: startDate,
                end_date: endDate,
            });
            handleClose();
        } catch (err) {
            setError('Error fetching device change log data');
            console.error(err);
        }
    };
    const totalDevices = (activeDeviceCount || 0) + (inActiveDeviceCount || 0) + (nonFunctionalDeviceCount || 0);

    const activePct = totalDevices ? Math.round(((activeDeviceCount || 0) / totalDevices) * 100) : 0;
    const inactivePct = totalDevices ? Math.round(((inActiveDeviceCount || 0) / totalDevices) * 100) : 0;
    const nonFunctionalPct = totalDevices ? Math.round(((nonFunctionalDeviceCount || 0) / totalDevices) * 100) : 0;

    const pieData = {
        labels: ['Active Device', 'Inactive Device', 'Non Functional'],
        datasets: [
            {
                data: [
                    activeDeviceCount || 0,
                    inActiveDeviceCount || 0,
                    nonFunctionalDeviceCount || 0,
                ],
                backgroundColor: [
                    '#2563EB', // Active (Blue)
                    '#EA580C', // Inactive (Orange)
                    '#0D9488', // Non Functional (Teal)
                ],
                borderWidth: 0,
                hoverOffset: 4,
            },
        ],
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '72%',
        plugins: {
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function (tooltipItem: any) {
                        const label = tooltipItem.label || '';
                        const value = tooltipItem.raw || 0;
                        const pct = totalDevices ? ((value / totalDevices) * 100).toFixed(1) : 0;
                        return ` ${label}: ${value} (${pct}%)`;
                    }
                }
            },
            legend: {
                display: false,
            },
        },
    };

    // Custom plugin to display "No data found" text inside line chart if needed
    const noDataPlugin = {
        id: 'noDataPlugin',
        beforeDraw: (chart: any) => {
            const { datasets } = chart.data;
            if (datasets[0].data.every((value: number) => value === 0)) {
                const ctx = chart.ctx;
                const width = chart.width;
                const height = chart.height;

                ctx.restore();
                ctx.font = '16px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = 'grey';
                ctx.fillText('No data found', width / 2, height / 2);
                ctx.save();
            }
        },
    };

    // Check if there is actual volume consumed data to render
    const hasInfusionData = Object.values(chartData).length > 0 && Object.values(chartData).some(v => (v as number) > 0);

    // Define the line chart options with smooth curve and area fill
    const data = {
        labels: Object.keys(chartData),
        datasets: [
            {
                label: 'Volume Consumed (ml)',
                data: Object.values(chartData),
                fill: true,
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                borderColor: '#2563EB',
                borderWidth: 2,
                tension: 0.35,
                pointRadius: 0,
                pointHoverRadius: 5,
            },
        ],
    };

    const options: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#0F172A',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 10,
                cornerRadius: 8,
            }
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: {
                    color: '#94A3B8',
                    font: { size: 10 },
                },
            },
            y: {
                grid: {
                    color: '#F1F5F9',
                },
                beginAtZero: true,
                ticks: {
                    color: '#94A3B8',
                    font: { size: 10 },
                }
            },
        },
    };

    return (
        <div
            style={{
                display: 'flex',
                gap: '20px',
                width: '100%',
                marginTop: '16px',
                flexWrap: 'wrap',
            }}
        >
            {/* ── Left Card: Overall Device Chart ── */}
            <div
                style={{
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '24px',
                    flex: '1 1 390px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <div>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                        <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', margin: 0 }}>
                            {userRole.toLowerCase() === NURSE.toLowerCase() ? t('analytics.title1') : t('analytics.title')}
                        </h2>
                    </div>

                    {/* Chart + Custom Legend Row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'space-between' }}>
                        {/* Doughnut Wrapper */}
                        <div style={{ position: 'relative', width: '160px', height: '160px', flexShrink: 0 }}>
                            <Doughnut data={pieData} options={pieOptions} />
                            {/* Absolute center label */}
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    textAlign: 'center',
                                    pointerEvents: 'none',
                                }}
                            >
                                <div style={{ fontSize: '28px', fontWeight: '800', color: '#0F172A', lineHeight: 1 }}>
                                    {totalDevices}
                                </div>
                                <div style={{ fontSize: '9px', color: '#94A3B8', fontWeight: '700', textTransform: 'uppercase', marginTop: '4px', letterSpacing: '0.5px' }}>
                                    Devices
                                </div>
                            </div>
                        </div>

                        {/* Custom Legend */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', flex: 1 }}>
                            {/* Active Device */}
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#2563EB', display: 'inline-block' }} />
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Active Device</span>
                                </div>
                                <span style={{ fontSize: '12px', color: '#94A3B8', paddingLeft: '16px', fontWeight: 500 }}>
                                    {activeDeviceCount || 0} <span style={{ color: '#CBD5E1', margin: '0 4px' }}>|</span> {activePct}%
                                </span>
                            </div>

                            {/* Inactive Device */}
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#EA580C', display: 'inline-block' }} />
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Inactive Device</span>
                                </div>
                                <span style={{ fontSize: '12px', color: '#94A3B8', paddingLeft: '16px', fontWeight: 500 }}>
                                    {inActiveDeviceCount || 0} <span style={{ color: '#CBD5E1', margin: '0 4px' }}>|</span> {inactivePct}%
                                </span>
                            </div>

                            {/* Non Functional */}
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0D9488', display: 'inline-block' }} />
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Non Functional</span>
                                </div>
                                <span style={{ fontSize: '12px', color: '#94A3B8', paddingLeft: '16px', fontWeight: 500 }}>
                                    {nonFunctionalDeviceCount || 0} <span style={{ color: '#CBD5E1', margin: '0 4px' }}>|</span> {nonFunctionalPct}%
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom monitoring card / banner */}
                <div
                    style={{
                        background: '#F8FAFC',
                        borderRadius: '12px',
                        padding: '12px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginTop: '24px',
                        border: '1px solid #F1F5F9',
                    }}
                >
                    <div
                        style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '10px',
                            background: 'rgba(37,99,235,0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                        </svg>
                    </div>
                    <p style={{ fontSize: '11px', color: '#475569', margin: 0, lineHeight: 1.4, fontWeight: 500, flex: 1 }}>
                        Keep monitoring your devices - Ensure all devices are functioning properly.
                    </p>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </div>
            </div>

            {/* ── Right Card: Infusion Data ── */}
            <div
                style={{
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '24px',
                    flex: '2 1 608px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                {/* Header Row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', margin: 0 }}>
                        {t('analytics.title2')}
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {/* Period select */}
                        <select
                            style={{
                                border: '1.5px solid #E2E8F0',
                                borderRadius: '8px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#475569',
                                background: '#fff',
                                cursor: 'pointer',
                                outline: 'none',
                            }}
                            name="type"
                            onChange={handleSelectChange}
                            value={selectedPeriod}
                            aria-label={t('analytics.select_period')}
                        >
                            <option value="today">{t('analytics.today')}</option>
                            <option value="this_week">{t('analytics.this_week')}</option>
                            <option value="last_week">{t('analytics.last_week')}</option>
                            <option value="this_month">{t('analytics.this_month')}</option>
                            <option value="last_month">{t('analytics.last_month')}</option>
                        </select>

                        {/* Filter dropdown button */}
                        <Dropdown show={isDropdownVisible} onToggle={handleToggle}>
                            <Dropdown.Toggle
                                as={React.forwardRef(({ children, onClick }: any, ref: any) => (
                                    <button
                                        ref={ref}
                                        onClick={onClick}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            background: '#2563EB',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: '7px 14px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 6px rgba(37,99,235,0.2)',
                                        }}
                                    >
                                        <FaFilter size={10} />
                                        Filter
                                    </button>
                                ))}
                            />
                            <Dropdown.Menu style={{ border: 'none', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', borderRadius: '12px', padding: '16px', width: '280px', zIndex: 1000 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A' }}>
                                        {t('analytics.filter')}
                                    </span>
                                    <button
                                        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                                        onClick={handleClose}
                                        aria-label="Close filter"
                                    >
                                        <CloseIcon />
                                    </button>
                                </div>
                                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div>
                                        <label htmlFor="device_id" style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748B', marginBottom: '4px' }}>
                                            {t('report.select_device_id')}
                                        </label>
                                        <Select
                                            id="device_id"
                                            name="device_id"
                                            options={deviceData.map((device) => ({
                                                value: device.device_id,
                                                label: `${device.device_name} (${device.device_id})`,
                                            }))}
                                            value={selectedDeviceOption}
                                            onChange={(selectedOption: SingleValue<DeviceOption>) =>
                                                setSelectedDeviceOption(selectedOption)
                                            }
                                            placeholder="Select The Device ID"
                                            styles={{
                                                control: (base) => ({
                                                    ...base,
                                                    fontSize: '12px',
                                                    borderColor: '#E2E8F0',
                                                    borderRadius: '6px',
                                                }),
                                            }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label htmlFor="startDate" style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748B', marginBottom: '4px' }}>
                                                Start Date
                                            </label>
                                            <input
                                                type="date"
                                                id="startDate"
                                                style={{ width: '100%', fontSize: '12px', padding: '6px', border: '1px solid #E2E8F0', borderRadius: '6px' }}
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label htmlFor="endDate" style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748B', marginBottom: '4px' }}>
                                                End Date
                                            </label>
                                            <input
                                                type="date"
                                                id="endDate"
                                                style={{ width: '100%', fontSize: '12px', padding: '6px', border: '1px solid #E2E8F0', borderRadius: '6px' }}
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <button type="submit" style={{ background: '#2563EB', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', marginTop: '6px' }}>
                                        {t('report.submit')}
                                    </button>
                                </form>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>

                {/* Chart or High Fidelity Empty State */}
                <div style={{ height: '210px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {hasInfusionData ? (
                        <div style={{ width: '100%', height: '100%' }}>
                            <Line data={data} options={options} />
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '8px' }}>
                            <svg width="88" height="88" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <ellipse cx="60" cy="106" rx="36" ry="5" fill="#F1F5F9" />
                                <path d="M25 32C25 28.6863 27.6863 26 31 26H50L58 35H89C92.3137 35 95 37.6863 95 41V88C95 91.3137 92.3137 94 89 94H31C27.6863 94 25 91.3137 25 88V32Z" fill="#EFF6FF" stroke="#3B82F6" strokeWidth="2" strokeLinejoin="round" />
                                <rect x="38" y="44" width="44" height="36" rx="4" fill="#FFFFFF" stroke="#93C5FD" strokeWidth="1.5" />
                                <rect x="46" y="64" width="6" height="10" rx="1.5" fill="#3B82F6" />
                                <rect x="56" y="54" width="6" height="20" rx="1.5" fill="#60A5FA" />
                                <rect x="66" y="60" width="6" height="14" rx="1.5" fill="#93C5FD" />
                                <circle cx="78" cy="74" r="14" fill="#FFFFFF" stroke="#2563EB" strokeWidth="2.5" />
                                <line x1="87" y1="83" x2="96" y2="92" stroke="#2563EB" strokeWidth="3" strokeLinecap="round" />
                                <circle cx="78" cy="74" r="9" fill="#DBEAFE" opacity="0.6" />
                            </svg>
                            <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0F172A', margin: '4px 0 0 0' }}>
                                No data found
                            </h3>
                            <p style={{ fontSize: '11px', color: '#94A3B8', margin: 0, maxWidth: '280px', lineHeight: 1.4 }}>
                                There is no infusion data available for the selected period.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Analyticschart;
